import distance from 'jaro-winkler';
import { captureException } from '@/utils/sentry';
import { Coordinate } from '@usepepper/ui/molecules/map/types';
import {
  NearbyBusiness,
  NearbyBusinessFilters,
  PAGE_SIZE,
  SortOrder,
} from '@/experiences/sales-portal/domain/prospects/blocs/loaders/use-prospect-finder-nearby-businesses-loader';
import { SalesPortalProspectFinderNearbyBusinessesDocument } from '@/experiences/sales-portal/domain/prospects/blocs/documents/sales-portal-prospect-finder-nearby-businesses.generated';
import {
  SalesPortalProspectFinderNearbyBusinessesQuery,
  SalesPortalProspectFinderNearbyBusinessesQueryVariables,
} from '@/types';
import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

const DIRECT_HIT_THRESHOLD = 0.9;

// Progressive widening for a business search: tight + distance-sorted first,
// then wider, then drop the cap and rank by best match.
const BUSINESS_ATTEMPTS: { radius: string | null; sortBy: SortOrder }[] = [
  { radius: '100', sortBy: SortOrder.DISTANCE },
  { radius: '1000', sortBy: SortOrder.DISTANCE },
  { radius: null, sortBy: SortOrder.BEST_MATCH },
];

const isSamePlace = (businessName: string, placeName: string) =>
  distance(businessName.toLowerCase().trim(), placeName.toLowerCase().trim()) >
  DIRECT_HIT_THRESHOLD;

/**
 * Probes Yelp at a resolved place location with a progressively widening
 * radius (up to 3 attempts) for a Jaro-Winkler name match. Returns the
 * matching row or null. Runs in the background so the caller can fly the
 * map first.
 */
export const useDirectHitRetry = (
  supplierUUID: string,
  filters: NearbyBusinessFilters,
) => {
  const client = useApolloClient();

  const fetchNearby = useCallback(
    async (location: Coordinate, radius: string | null, sortBy: SortOrder) => {
      const { data } = await client.query<
        SalesPortalProspectFinderNearbyBusinessesQuery,
        SalesPortalProspectFinderNearbyBusinessesQueryVariables
      >({
        fetchPolicy: 'network-only',
        query: SalesPortalProspectFinderNearbyBusinessesDocument,
        variables: {
          ...filters,
          latitude: location.latitude,
          limit: PAGE_SIZE,
          longitude: location.longitude,
          offset: 0,
          radius,
          sortBy,
          supplierUUID,
        },
      });
      return (data.getNearbyBusinessesV2 ?? []).map<NearbyBusiness>((b) => ({
        businessName: b.business_name,
        categories: b.categories ?? undefined,
        displayAddress: b.business_display_address,
        distance: b.distance ?? undefined,
        imageUrl: b.image_url ?? undefined,
        latitude: b.latitude,
        longitude: b.longitude,
        restaurantUuid: b.restaurant_uuid ?? undefined,
        status: b.status ?? undefined,
        supplierUuid: b.supplier_uuid ?? undefined,
        yelpBusinessId: b.yelp_business_id,
      }));
    },
    [client, filters, supplierUUID],
  );

  const findDirectHit = useCallback(
    async (
      location: Coordinate,
      label: string,
    ): Promise<NearbyBusiness | null> => {
      try {
        for (const { radius, sortBy } of BUSINESS_ATTEMPTS) {
          const rows = await fetchNearby(location, radius, sortBy);
          const hit = rows.find((row) => isSamePlace(row.businessName, label));
          if (hit) {
            return hit;
          }
        }
      } catch (error) {
        // A probe failed; fall through silently — caller already flew the map.
        captureException(error);
      }
      return null;
    },
    [fetchNearby],
  );

  return { findDirectHit };
};
